import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { calculateMatrix, type MatrixDetailsResult } from '../utils/numerology';
import './MatrixDetails.scss';

interface MatrixDetailsProps {
  name: string;
  birthDate: string;
}

const MatrixDetails: React.FC<MatrixDetailsProps> = ({ name, birthDate }) => {
  const { t, i18n } = useTranslation();
  const [details, setDetails] = useState<MatrixDetailsResult | null>(null);
  const [expandedSection, setExpandedSection] = useState<string | null>('destiny');

  useEffect(() => {
    const result = calculateMatrix(name, birthDate, i18n.language);
    setDetails(result.details);
  }, [name, birthDate, i18n.language]);

  if (!details) {
    return (
      <div className="matrix-details matrix-details--loading">
        <div className="matrix-details__loader" />
        <p>{t('matrix.loading')}</p>
      </div>
    );
  }

  const sections = [
    { id: 'destiny', title: t('matrix.details.destiny'), content: details.destinyText, icon: '◆', highlight: true },
    { id: 'lifePath', title: t('matrix.details.lifePath'), content: details.lifePath, icon: '◇' },
    { id: 'character', title: t('matrix.details.character'), content: details.characterLine, icon: '1' },
    { id: 'energy', title: t('matrix.details.energy'), content: details.energyLine, icon: '2' },
    { id: 'mind', title: t('matrix.details.mind'), content: details.mindLine, icon: '3' },
    { id: 'temperament', title: t('matrix.details.temperament'), content: details.temperament, icon: '△' },
    { id: 'purpose', title: t('matrix.details.purpose'), content: details.purpose, icon: '▽' },
    { id: 'strong', title: t('matrix.details.strongNumbers'), content: details.strongNumbersText, icon: '★' },
    { id: 'karma', title: t('matrix.details.karma'), content: details.karmaText, icon: '◎' },
  ];

  return (
    <div className="matrix-details">
      <div className="matrix-details__header">
        <h2 className="matrix-details__title">{t('matrix.details.title')}</h2>
        <div className="matrix-details__badge">
          <span className="matrix-details__number">{details.destinyNumber}</span>
          <span className="matrix-details__label">{t('matrix.details.destinyNumber')}</span>
        </div>
      </div>

      <div className="matrix-details__sections">
        {sections.map(({ id, title, content, icon, highlight }) => (
          <div
            key={id}
            className={`matrix-details__card ${expandedSection === id ? 'matrix-details__card--open' : ''} ${highlight ? 'matrix-details__card--highlight' : ''}`}
            onClick={() => setExpandedSection(expandedSection === id ? null : id)}
          >
            <div className="matrix-details__card-header">
              <span className="matrix-details__card-icon">{icon}</span>
              <h3 className="matrix-details__card-title">{title}</h3>
              <span className="matrix-details__card-toggle">{expandedSection === id ? '−' : '+'}</span>
            </div>
            {expandedSection === id && (
              <div className="matrix-details__card-body">
                <p>{content}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="matrix-details__digits">
        <h3 className="matrix-details__digits-title">{t('matrix.details.digitByDigit')}</h3>
        <div className="matrix-details__digits-grid">
          {details.digitInterpretations.map((d) => (
            <div
              key={d.number}
              className={`matrix-details__digit-card ${d.count > 0 ? 'matrix-details__digit-card--filled' : ''} ${d.count >= 2 ? 'matrix-details__digit-card--strong' : ''}`}
            >
              <div className="matrix-details__digit-header">
                <span className="matrix-details__digit-num">{d.number}</span>
                {d.count > 0 && <span className="matrix-details__digit-count">×{d.count}</span>}
              </div>
              <p className="matrix-details__digit-desc">{d.shortDescription}</p>
              {d.byCount && <p className="matrix-details__digit-bycount">{d.byCount}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MatrixDetails;
